/**
 * The rule's "only if": conditions on the row, checked once the trigger has
 * matched. Drawn with the board's own advanced-filter rows, because the engine
 * reads a condition exactly as the filter reads the same expression.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { TrackerBoardType } from '@/entities/tracker';
import type { AutomationConditions } from '@/entities/tracker-automation';
import { workspaceUsersQuery } from '@/entities/project-member';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import { conditionableColumns, conditionsAllowNegation } from '../../lib/automation-model';
import {
  FILTER_OPERATORS,
  buildFilterColumns,
  readCustomColumnValue,
  type FilterColumnSpec,
} from '../../lib/filtering';
import { useFilterOptionLabels } from '../../lib/useFilterOptionLabels';
import type { CustomColumn } from '../../lib/useTrackersBoard';
import { FilterRuleRow } from '../FilterRuleRow';

interface AutomationConditionsBlockProps {
  conditions: AutomationConditions | null;
  onChange: (next: AutomationConditions | null) => void;
  columns: CustomColumn[];
  boardType: TrackerBoardType;
}

export function AutomationConditionsBlock({
  conditions,
  onChange,
  columns,
  boardType,
}: AutomationConditionsBlockProps) {
  const { t } = useTranslation('trackers');
  const specs = useConditionColumnSpecs(columns);
  const operators = conditionsAllowNegation(boardType)
    ? FILTER_OPERATORS
    : FILTER_OPERATORS.filter((o) => o === 'is');

  const match = conditions?.match ?? 'all';
  const rules = conditions?.rules ?? [];
  // An empty list is stored as no conditions, so a rule reads the same however
  // its last condition went away.
  const setRules = (next: AutomationConditions['rules']) =>
    onChange(next.length > 0 ? { match, rules: next } : null);

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('trackersBoard.automationRuleFields.onlyIf')}
        </span>
        {rules.length > 1 ? (
          <Select
            value={match}
            onValueChange={(next) =>
              onChange({ match: next as AutomationConditions['match'], rules })
            }
          >
            <SelectTrigger
              className="h-8 w-44"
              aria-label={t('trackersBoard.automationRuleFields.conditionsMatch')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* eslint-disable-next-line i18next/no-literal-string -- match id */}
              <SelectItem value="all">
                {t('trackersBoard.automationRuleFields.allOfThese')}
              </SelectItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- match id */}
              <SelectItem value="any">
                {t('trackersBoard.automationRuleFields.anyOfThese')}
              </SelectItem>
            </SelectContent>
          </Select>
        ) : null}
      </div>

      {/* Stored conditions render even when no column can take one any more,
          so a condition on a deleted column can still be removed. */}
      <div className="flex flex-col items-start gap-3">
        {rules.length === 0 && specs.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('trackersBoard.automationRuleFields.runsOnEveryMatchingRow')}
          </p>
        ) : (
          rules.map((rule, index) => (
            <FilterRuleRow
              // Conditions carry no id of their own and are never reordered.
              key={index}
              rule={rule}
              columns={specs}
              operators={operators}
              label={
                index === 0
                  ? t('trackersBoard.trackersFilterPanel.where')
                  : match === 'any'
                    ? t('trackersBoard.automationRuleFields.or')
                    : t('trackersBoard.trackersFilterPanel.and')
              }
              onChange={(patch) =>
                setRules(
                  rules.map((r, i) =>
                    i === index ? { ...r, ...patch, values: [...(patch.values ?? r.values)] } : r,
                  ),
                )
              }
              onRemove={() => setRules(rules.filter((_, i) => i !== index))}
            />
          ))
        )}
        {specs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('trackersBoard.automationRuleFields.noColumnToCheck')}
          </p>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="mini"
            className="text-muted-foreground hover:text-foreground"
            onClick={() =>
              setRules([...rules, { columnId: specs[0].id, operator: 'is', values: [] }])
            }
          >
            <Plus className="size-3.5" aria-hidden />
            {t('trackersBoard.automationRuleFields.addCondition')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The columns a condition can name, built exactly as the board filter builds
 * them, except that people come from the whole workspace: a rule is written for
 * rows that do not exist yet.
 */
function useConditionColumnSpecs(columns: CustomColumn[]): FilterColumnSpec[] {
  const labels = useFilterOptionLabels();
  const workspaceId = useCurrentWorkspaceId();
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId),
  });

  return useMemo(() => {
    const people = (usersQuery.data?.data ?? [])
      .map((u) => ({ value: u.id, label: u.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return buildFilterColumns(
      conditionableColumns(columns),
      [],
      readCustomColumnValue,
      labels,
      people,
    );
  }, [columns, labels, usersQuery.data]);
}
